import clsx from 'clsx'
import { ptBR as fnsPtBR } from 'date-fns/locale/pt-BR'
import * as echarts from 'echarts'
import React from 'react'
import { classToFComponent } from 'src/utils/views'
import { makeStyles } from 'tss-react/mui'

import { DateFns } from 'lang-utils'
import { EChartViewClass } from 'src/modules/the-consumer-market/views/helpers/base-echart'
import { parseLocalDateTimeNoTZ } from 'src/utils/formatter-utils'
import { ChartHistoryScope } from '../../gp_scopes'
import ProfileCard from './profile-card'

const fns = DateFns.singleton

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        alignSelf: 'stretch',
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
        overflowY: 'visible',
        overflowX: 'hidden',
        '& .chart-holder': {}
    },
    chart: {}
})

type ChartConsumoFranquiaProps = {
    className?: string
    style?: React.CSSProperties
    scope: ChartHistoryScope
}

class ChartConsumoFranquiaViewClass extends EChartViewClass<ChartConsumoFranquiaProps> {
    // :: Fields
    scope!: ChartHistoryScope
    classes!: ReturnType<typeof useStyles>['classes']

    onSyncState(props: ChartConsumoFranquiaProps) {
        this.scope = props.scope
        this.classes = useStyles().classes
        this.chartData = buildChartData(this.scope)
        this.loaded = !this.scope.history_chart_working
        super.onSyncState(props)
    }

    override render({ className, style, scope }: ChartConsumoFranquiaProps): React.JSX.Element {
        return (
            <ProfileCard caption="Consumo franquia exportação por mês" className={clsx(className)} style={style}>
                {super.render({ className: this.classes.view, scope })}
            </ProfileCard>
        )
    }

    protected override onComputeSize(opts: echarts.ResizeOpts) {
        if (this.scope.historyChart?.records?.length > 0) {
            if (this.scope.historyChart.records.length > 10) {
                opts.height = Math.max(this.scope.historyChart.records.length * 30, opts.height as number)
            }
        }
    }
}

export default classToFComponent(ChartConsumoFranquiaViewClass, React)

function buildChartData(scope: ChartHistoryScope): echarts.EChartsOption {
    const xAxisData = generateXData(scope)
    const series = buildChartSeries(
        scope,
        xAxisData.map((d) => d.key)
    )

    return {
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'shadow' }
        },
        legend: {
            bottom: 50
        },
        grid: { containLabel: true, top: 20, left: 10, right: 10, bottom: 100 },
        xAxis: {
            type: 'category',
            axisTick: {
                alignWithLabel: true
            },
            axisLabel: {
                show: true,
                rotate: 30
            },
            data: xAxisData
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                width: 170,
                fontSize: 8
            },
            splitLine: {
                show: false
            },
            boundaryGap: [0, 0.01]
        },
        series
    }
}

function generateXData(scope: ChartHistoryScope) {
    const xAxisData: {
        key: string
        value: string
    }[] = []

    scope.historyChart?.records?.forEach(
        (r) =>
            !xAxisData.find((d) => d.key === r.mes_ano) &&
            xAxisData.push({
                key: r.mes_ano,
                value: fns().formatDate(parseLocalDateTimeNoTZ(r.mes_ano), 'MMM/yy', { locale: fnsPtBR })
            })
    )
    return xAxisData
}

function buildChartSeries(scope: ChartHistoryScope, xkeys: string[]): echarts.SeriesOption[] {
    const franquiaData: {
        id: string
        value: number
    }[] = []

    const yAxisData: {
        id: string
        origem?: string
        value: number
    }[] = []

    for (const xKey of xkeys || []) {
        franquiaData.push({
            id: xKey,
            value: scope.historyChart?.records?.find((r) => r.mes_ano === xKey && !r.origem)?.new_limit_export || 0
        })
    }

    for (const row of scope.historyChart?.records || []) {
        yAxisData.push({
            id: row.mes_ano,
            origem: row.origem,
            value: row.qtd_registros ?? 0
        })
    }

    const series = [
        {
            type: 'line',
            name: 'Contratado',
            smooth: true,
            label: {
                show: true,
                align: 'left',
                position: 'top'
            },
            data: franquiaData
        }
    ] as echarts.SeriesOption[]

    for (const origemName of [...new Set(yAxisData.map((y) => y.origem))]) {
        series.push({
            name: origemName || 'Total de Registros Exportados',
            type: 'bar',
            label: {
                show: true,
                align: 'left',
                position: 'top'
            },
            data: xkeys.map((k) => yAxisData.find((y) => y.id === k && y.origem === origemName) || null),
            stack: origemName ? 'YM' : undefined,
            barGap: !origemName ? '0' : '-100%'
        })
    }

    return series
}
